/**
 * MobileBarSection — Paramètres › Barre mobile (Batch #14.322).
 * Deux barres : admin (Accueil · ≤ 3 raccourcis · Menu) et portail client (Accueil · ≤ 4 tuiles, filtrées par les accès de chaque client).
 */
import { useState } from "react";
import { Home, Menu, Save } from "lucide-react";
import { toast } from "@/lib/toast";
import {
  ADMIN_MOBILE_DEFAULT, ADMIN_MOBILE_MAX, ADMIN_MOBILE_TABS, PORTAL_MOBILE_DEFAULT, PORTAL_MOBILE_MAX, PORTAL_MOBILE_TABS, resolveTabs,
} from "@/constants/mobileTabs";

function BarPreview({ tabs, withMenu, testId }) {
  const Item = ({ label, icon: Icon, active }) => (
    <span className={`flex-1 flex flex-col items-center gap-0.5 py-1.5 text-[10px] ${active ? "text-white" : "text-white/60"}`}>
      <span className={`w-9 h-6 rounded-full flex items-center justify-center ${active ? "bg-white/20" : ""}`}><Icon className="w-[18px] h-[18px]" /></span>{label}
    </span>
  );
  return (
    <div className="max-w-sm rounded-xl overflow-hidden bg-gradient-to-br from-[#072354] to-[#2877D1] flex items-stretch" data-testid={testId}>
      <Item label="Accueil" icon={Home} active />
      {tabs.map((t) => <Item key={t.id} label={t.label} icon={t.icon} />)}
      {withMenu && <Item label="Menu" icon={Menu} />}
    </div>
  );
}

function Picker({ title, hint, catalog, ids, setIds, max, fallback, withMenu, testPrefix }) {
  const selected = resolveTabs(catalog, ids, [], max);
  const toggle = (id) => {
    if (ids.includes(id)) return setIds(ids.filter((x) => x !== id));
    if (ids.length >= max) return toast.error(`Maximum ${max} raccourcis — retirez-en un d'abord`);
    setIds([...ids, id]);
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 space-y-4" data-testid={`${testPrefix}-card`}>
      <div>
        <h3 className="font-semibold text-slate-800">{title}</h3>
        <p className="text-xs text-slate-500 mt-0.5">{hint}</p>
      </div>
      <BarPreview tabs={selected} withMenu={withMenu} testId={`${testPrefix}-preview`} />
      <div className="flex flex-wrap gap-2">
        {catalog.map((t) => {
          const idx = ids.indexOf(t.id);
          const on = idx >= 0;
          return (
            <button key={t.id} type="button" onClick={() => toggle(t.id)} aria-pressed={on}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition active:scale-95 ${on ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
              data-testid={`${testPrefix}-opt-${t.id}`}>
              {on && <span className="w-4 h-4 rounded-full bg-white/20 text-[10px] flex items-center justify-center">{idx + 1}</span>}
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span data-testid={`${testPrefix}-count`}>{ids.length} / {max} sélectionné(s) — l'ordre des numéros est l'ordre dans la barre</span>
        <button type="button" onClick={() => setIds(fallback)} className="underline hover:text-slate-800" data-testid={`${testPrefix}-reset`}>Valeurs par défaut</button>
      </div>
    </div>
  );
}

export default function MobileBarSection({ settings, update, onSave }) {
  const adminIds = Array.isArray(settings.mobile_tabs) && settings.mobile_tabs.length ? settings.mobile_tabs : ADMIN_MOBILE_DEFAULT;
  const portalIds = Array.isArray(settings.portal_mobile_tabs) && settings.portal_mobile_tabs.length ? settings.portal_mobile_tabs : PORTAL_MOBILE_DEFAULT;
  const [busy, setBusy] = useState(false);
  if (!settings._ts) return <div className="text-sm text-slate-500 py-8 text-center" data-testid="mobile-bar-loading">Chargement des réglages…</div>;

  const save = async () => {
    setBusy(true);
    try { await onSave(["mobile_tabs", "portal_mobile_tabs"]); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="mobile-bar-section">
      <Picker title="Barre admin (téléphone & tablette)" hint="Accueil et Menu sont toujours présents. Choisissez jusqu'à 3 raccourcis entre les deux ; la barre s'affiche sous la largeur « lg »."
        catalog={ADMIN_MOBILE_TABS} ids={adminIds} setIds={(v) => update("mobile_tabs", v.length ? v : ADMIN_MOBILE_DEFAULT)} max={ADMIN_MOBILE_MAX} fallback={ADMIN_MOBILE_DEFAULT} withMenu testPrefix="mobile-admin" />
      <Picker title="Barre du portail client (téléphone)" hint="Accueil est toujours présent. Choisissez jusqu'à 4 tuiles : chaque client ne voit que celles auxquelles il a accès (Gestion des accès)."
        catalog={PORTAL_MOBILE_TABS} ids={portalIds} setIds={(v) => update("portal_mobile_tabs", v.length ? v : PORTAL_MOBILE_DEFAULT)} max={PORTAL_MOBILE_MAX} fallback={PORTAL_MOBILE_DEFAULT} testPrefix="mobile-portal" />
      <div className="flex justify-end">
        <button type="button" onClick={save} disabled={busy} className="px-5 py-2.5 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition active:scale-95 inline-flex items-center gap-2 disabled:opacity-60"
          style={{ background: "#072354" }} data-testid="mobile-bar-save">
          <Save className="w-4 h-4" /> Sauvegarder
        </button>
      </div>
    </div>
  );
}
